import React, { useState } from "react";
import { Download, Star, Heart, Zap, Book, Headphones, Palette, Briefcase } from "lucide-react";
import { Button } from "./ui/button";

const Resources = () => {
  const [downloadingId, setDownloadingId] = useState(null);

  const resources = [
    {
      id: 1,
      title: "888 Transformation Guide",
      description: "Complete spiritual awakening roadmap with ancient principles for modern seekers",
      category: "Spiritual Growth",
      icon: <Star size={24} className="text-yellow-400" />,
      fileType: "PDF Guide",
      featured: true,
      color: "from-yellow-400 to-orange-500"
    },
    {
      id: 2,
      title: "<strong>YAH HEALTH WEALTH HAPPINESS</strong> Workbook",
      description: "Interactive journal for manifesting the universal pillars of success",
      category: "Holistic Living",
      icon: <Heart size={24} className="text-red-400" />,
      fileType: "Interactive PDF",
      featured: true,
      color: "from-red-400 to-pink-500"
    },
    {
      id: 3,
      title: "A.R.T. Energi Daily Practice Cards",
      description: "Authenticity, Reliability, Truth affirmations and mobile wallpapers",
      category: "Daily Practice",
      icon: <Zap size={24} className="text-purple-400" />,
      fileType: "Digital Cards",
      featured: false,
      color: "from-purple-400 to-indigo-500"
    },
    {
      id: 4,
      title: "FLY FREQUENCY Business Framework",
      description: "Faith.Love.Yah methodology for integrating spirituality in professional life",
      category: "Professional Development",
      icon: <Briefcase size={24} className="text-blue-400" />,
      fileType: "Strategy Guide",
      featured: true,
      color: "from-blue-400 to-cyan-500"
    },
    {
      id: 5,
      title: "1010 Portal Meditation Audio",
      description: "Guided spiritual practice for entering transformation consciousness",
      category: "Meditation",
      icon: <Headphones size={24} className="text-green-400" />,
      fileType: "Audio MP3",
      featured: false,
      color: "from-green-400 to-teal-500"
    },
    {
      id: 6,
      title: "INSPIYAH Culture Manifesto",
      description: "Sacred streetwear brand philosophy and styling guide for spiritual warriors",
      category: "Brand Culture",
      icon: <Palette size={24} className="text-orange-400" />,
      fileType: "Lookbook PDF",
      featured: false,
      color: "from-orange-400 to-red-500"
    }
  ];

  const categories = ["All Resources", "Spiritual Growth", "Holistic Living", "Daily Practice", "Professional Development", "Meditation", "Brand Culture"];
  const [selectedCategory, setSelectedCategory] = useState("All Resources");

  const filteredResources = selectedCategory === "All Resources" 
    ? resources 
    : resources.filter(resource => resource.category === selectedCategory);

  const featuredResources = resources.filter(resource => resource.featured);

  const handleDownload = (resourceId) => {
    setDownloadingId(resourceId);
    // Mock download process
    setTimeout(() => {
      setDownloadingId(null);
      // In real implementation, this would trigger actual download
      console.log(`Downloading resource ${resourceId}`);
    }, 2000);
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Mystical Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-black to-orange-900">
        <div className="absolute inset-0 bg-gradient-to-tr from-yellow-500/10 via-purple-500/15 to-orange-500/10 animate-pulse" style={{animationDuration: '6s'}} />
        
        {/* Sacred geometry background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 border-2 border-yellow-400 rotate-45 animate-spin" style={{animationDuration: '20s'}} />
          <div className="absolute bottom-32 right-32 w-24 h-24 border border-orange-400 rounded-full animate-pulse" style={{animationDuration: '4s'}} />
          <div className="absolute top-1/2 left-1/4 w-40 h-40 border border-purple-400" style={{transform: 'rotate(45deg)'}} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Download size={24} className="text-yellow-400 animate-bounce" />
            <span className="text-sm uppercase tracking-widest text-orange-300 font-light">
              Sacred Resources
            </span>
            <Download size={24} className="text-orange-400 animate-bounce" />
          </div>
          
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight mb-6 relative">
            <span className="bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent">
              INSPIYAH Resources
            </span>
            <div className="absolute -inset-2 bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 blur-xl -z-10 animate-pulse" />
          </h2>
          
          <p className="text-lg max-w-3xl mx-auto font-light leading-relaxed">
            <span className="bg-gradient-to-r from-orange-200 to-yellow-200 bg-clip-text text-transparent">
              Free transformational tools and guides to accelerate your EPIIC HERO journey. 
              Each resource channels <span className="font-medium">A.R.T. Energi</span> and ancient wisdom for modern spiritual warriors.
            </span>
          </p>
        </div>

        {/* Featured Resources */}
        <div className="mb-16">
          <h3 className="text-2xl font-light text-center mb-8">
            <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
              Featured Downloads
            </span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredResources.map((resource) => (
              <div 
                key={resource.id}
                className="bg-gradient-to-br from-black/80 via-purple-900/60 to-orange-900/60 backdrop-blur-sm border border-yellow-400/20 rounded-2xl p-8 hover:border-yellow-400/40 transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-lg group-hover:from-yellow-400/40 group-hover:to-orange-400/40 transition-colors">
                    {resource.icon}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-orange-300">
                    {resource.category}
                  </div>
                </div>
                
                <h4 className="text-xl font-light text-white mb-3 leading-relaxed">
                  {resource.title}
                </h4>
                
                <p className="text-gray-300 font-light text-sm leading-relaxed mb-6">
                  {resource.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-purple-300 font-light">
                    {resource.fileType}
                  </span>
                  <Button
                    onClick={() => handleDownload(resource.id)}
                    disabled={downloadingId === resource.id}
                    className={`bg-gradient-to-r ${resource.color} text-black hover:scale-105 transition-transform duration-300 px-6 py-2`}
                  >
                    {downloadingId === resource.id ? (
                      <span className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </span>
                    ) : (
                      <span className="flex items-center space-x-2">
                        <Download size={16} />
                        <span>Download</span>
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full text-sm font-light tracking-wide transition-all duration-300 ${
                selectedCategory === category
                  ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-black shadow-lg"
                  : "bg-black/40 text-orange-200 hover:bg-gradient-to-r hover:from-yellow-400/20 hover:to-orange-400/20 border border-yellow-400/20"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* All Resources Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => (
            <div 
              key={resource.id}
              className="bg-black/60 backdrop-blur-sm border border-gray-600/30 rounded-xl p-6 hover:border-yellow-400/30 transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-gray-800/50 rounded-lg group-hover:bg-yellow-400/20 transition-colors">
                  {resource.icon}
                </div>
                <div className="text-xs uppercase tracking-widest text-gray-400">
                  {resource.category}
                </div>
              </div>
              
              <h4 className="text-lg font-light text-white mb-2">
                {resource.title}
              </h4>
              
              <p className="text-gray-400 font-light text-sm leading-relaxed mb-4">
                {resource.description}
              </p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{resource.fileType}</span>
                <button
                  onClick={() => handleDownload(resource.id)}
                  disabled={downloadingId === resource.id}
                  className="text-yellow-400 hover:text-orange-400 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Download size={14} />
                  <span>Get Now</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Sacred Footer Message */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-black/80 via-purple-900/80 to-orange-900/80 backdrop-blur-sm border border-yellow-400/20 rounded-2xl p-12">
            <div className="text-6xl mb-6 animate-pulse" style={{animationDuration: '3s'}}>
              ✨
            </div>
            <h3 className="text-2xl font-light mb-4">
              <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
                Transform Your Reality with Sacred Knowledge
              </span>
            </h3>
            <p className="text-orange-200 font-light max-w-2xl mx-auto leading-relaxed">
              Each resource carries the frequency of transformation. Download, practice, and watch your 
              EPIIC HERO journey unfold through the power of **<strong>YAH HEALTH WEALTH HAPPINESS</strong>**.
            </p>
            <div className="mt-6 text-sm text-purple-300 italic">
              "You are my witness declares YHWH יהוה" - Isaiah 43:10
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resources;